/**
 * OAuth2 testing module
 */

import { generateState } from './utils.js';

// Prevent multiple simultaneous tests
let isTestRunning = false;

export async function testOAuth2Flow() {
    const button = document.getElementById('testOAuth2');
    if (!button || isTestRunning) return;

    isTestRunning = true;
    const actionCard = button.closest('.action-card');

    // Find or create result container
    let resultContainer = actionCard?.querySelector('.oauth2-test-result');
    if (!resultContainer && actionCard) {
        resultContainer = document.createElement('div');
        resultContainer.className = 'oauth2-test-result';
        resultContainer.style.cssText = 'margin-top: 1rem; padding: 1rem; border-radius: 8px; font-family: monospace; font-size: 0.875rem; white-space: pre-wrap; word-break: break-all;';
        actionCard.querySelector('.action-content')?.appendChild(resultContainer);
    }

    if (!resultContainer) {
        resultContainer = document.getElementById('oauth2-result');
    }

    // Save original button state
    const originalHTML = button.innerHTML;
    const originalDisabled = button.disabled;

    // Show loading state
    button.innerHTML = '<span class="oauth2-spinner"></span> Testing...';
    button.disabled = true;

    // Add spinner CSS
    addSpinnerStyle();

    const results = [];
    let hasErrors = false;

    // Helper to update display
    const updateDisplay = (status) => {
        if (resultContainer) {
            resultContainer.textContent = results.join('\n');
            resultContainer.className = 'oauth2-test-result oauth2-' + status;
        }
    };

    try {
        // Test 1: OIDC Discovery
        results.push('Testing OIDC Discovery...');
        updateDisplay('testing');

        const discoveryResult = await testEndpoint('/.well-known/openid-configuration', 'GET');
        if (discoveryResult.ok) {
            try {
                const config = await discoveryResult.response.json();
                results[results.length - 1] = '✓ OIDC Discovery: OK';
                results.push(`  Issuer: ${config.issuer || 'N/A'}`);
            } catch {
                results[results.length - 1] = '✓ OIDC Discovery: OK';
            }
        } else {
            results[results.length - 1] = `✗ OIDC Discovery: ${discoveryResult.error}`;
            hasErrors = true;
        }
        updateDisplay(hasErrors ? 'error' : 'testing');

        // Test 2: OAuth2 Authorization
        results.push('');
        results.push('Testing OAuth2 Authorization...');
        updateDisplay('testing');

        const authResult = await testEndpoint('/oauth2/authorize?client_id=demo-client&response_type=code&redirect_uri=' +
            encodeURIComponent(window.location.origin + '/dashboard') + '&scope=openid&state=' + generateState(), 'GET');

        if (authResult.ok || authResult.status === 303 || authResult.status === 302 || authResult.status === 0) {
            results[results.length - 1] = '✓ OAuth2 Authorization: Ready';
        } else {
            results[results.length - 1] = `⚠ OAuth2 Authorization: ${authResult.error || 'HTTP ' + authResult.status}`;
        }
        updateDisplay(hasErrors ? 'error' : 'testing');

        // Test 3: Token Endpoint
        results.push('');
        results.push('Testing Token Endpoint...');
        updateDisplay('testing');

        const tokenResult = await testEndpoint('/oauth2/token', 'POST', 'grant_type=client_credentials');
        // 400/401 is expected - endpoint is working but needs auth
        if (tokenResult.status === 400 || tokenResult.status === 401 || tokenResult.ok) {
            results[results.length - 1] = '✓ Token Endpoint: Accessible';
        } else {
            results[results.length - 1] = `✗ Token Endpoint: ${tokenResult.error}`;
            hasErrors = true;
        }

        // Final summary
        results.push('');
        results.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        results.push(hasErrors ? '⚠ Some tests had issues' : '✓ All endpoints operational!');

        updateDisplay(hasErrors ? 'error' : 'success');

    } catch (error) {
        console.error('OAuth2 test error:', error);
        results.push(`\n✗ Error: ${error.message}`);
        updateDisplay('error');
    }

    // Always restore button
    button.innerHTML = originalHTML;
    button.disabled = originalDisabled;
    isTestRunning = false;
}

async function testEndpoint(url, method, body) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    try {
        const options = {
            method,
            signal: controller.signal,
            redirect: 'manual'
        };

        if (body) {
            options.headers = { 'Content-Type': 'application/x-www-form-urlencoded' };
            options.body = body;
        }

        const response = await fetch(url, options);
        clearTimeout(timeoutId);

        return {
            ok: response.ok || response.type === 'opaqueredirect',
            status: response.status,
            response
        };
    } catch (error) {
        clearTimeout(timeoutId);
        if (error.name === 'AbortError') {
            return { ok: false, error: 'Timeout', status: 0 };
        }
        return { ok: false, error: error.message, status: 0 };
    }
}

function addSpinnerStyle() {
    if (document.getElementById('oauth2-styles')) return;

    const style = document.createElement('style');
    style.id = 'oauth2-styles';
    style.textContent = `
        .oauth2-spinner {
            display: inline-block;
            width: 14px;
            height: 14px;
            border: 2px solid currentColor;
            border-top-color: transparent;
            border-radius: 50%;
            animation: oauth2-spin 0.8s linear infinite;
            vertical-align: middle;
            margin-right: 6px;
        }
        @keyframes oauth2-spin {
            to { transform: rotate(360deg); }
        }
        .oauth2-test-result {
            display: block;
            margin-top: 1rem;
            padding: 1rem;
            border-radius: 8px;
            font-family: monospace;
            font-size: 0.85rem;
            line-height: 1.5;
            white-space: pre-wrap;
        }
        .oauth2-testing {
            background: hsl(220 14% 96%);
            border: 1px solid hsl(220 13% 91%);
            color: hsl(220 9% 46%);
        }
        .oauth2-success {
            background: hsl(142 76% 96%);
            border: 1px solid hsl(142 76% 86%);
            color: hsl(142 76% 26%);
        }
        .oauth2-error {
            background: hsl(0 84% 96%);
            border: 1px solid hsl(0 84% 86%);
            color: hsl(0 84% 30%);
        }
    `;
    document.head.appendChild(style);
}

export function copyEndpoint(path) {
    const url = window.location.origin + path;
    navigator.clipboard.writeText(url).then(() => {
        const button = event?.target?.closest('button');
        if (!button) return;

        const originalHTML = button.innerHTML;
        button.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"></polyline></svg>`;
        button.style.color = 'hsl(142 76% 36%)';

        setTimeout(() => {
            button.innerHTML = originalHTML;
            button.style.color = '';
        }, 1500);
    }).catch(err => console.error('Copy failed:', err));
}

// Initialize
export function initOAuth2() {
    const button = document.getElementById('testOAuth2');
    if (button) {
        button.addEventListener('click', testOAuth2Flow);
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initOAuth2);
} else {
    initOAuth2();
}

window.testOAuth2Flow = testOAuth2Flow;
window.copyEndpoint = copyEndpoint;
